import { useState, useRef, useEffect, useMemo } from "react";
import {
  X,
  ChevronLeft,
  ChevronRight,
  Image,
  Upload,
  Loader2,
} from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { patchProjectKpis } from "@/lib/datasource";
import { getSupabase } from "@/lib/supabase/client";

interface Photo {
  id: string;
  url: string;
  caption?: string;
  fileName?: string;
  size?: number;
  uploadedAt?: string;
  isPreloaded?: boolean;
  source?: "file" | "url";
}

interface PhotoGalleryProps {
  photos: (Photo | string)[];
  onPhotosChange: (photos: (Photo | string)[]) => void;
  maxUploads?: number;
  dealId?: string;
  projectId?: string;
  mainPhotoUrl?: string;
}

export function PhotoGallery({
  photos = [],
  onPhotosChange,
  maxUploads = 10,
  dealId,
  projectId,
  mainPhotoUrl,
}: PhotoGalleryProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const supabase = getSupabase();

  const normalizedPhotos = useMemo(() => {
    return photos.map((photo, index) => {
      if (typeof photo === "string") {
        return {
          id: `photo-${index}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          url: photo,
          source: "url" as const,
          isPreloaded: photo === mainPhotoUrl,
        };
      }
      return photo;
    });
  }, [photos, mainPhotoUrl]);

  const displayPhotos = useMemo(() => {
    const combined = [...normalizedPhotos];
    if (mainPhotoUrl && !normalizedPhotos.some((p) => p.url === mainPhotoUrl)) {
      combined.unshift({
        id: "main-photo-url-virtual",
        url: mainPhotoUrl,
        caption: "Main Photo",
        source: "url",
        isPreloaded: true,
      });
    }
    return combined;
  }, [normalizedPhotos, mainPhotoUrl]);

  const uploadedCount = normalizedPhotos.length;
  const canUpload = uploadedCount < maxUploads;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxOpen || displayPhotos.length <= 1) return;
      if (e.key === "ArrowLeft") navigateLightbox("prev");
      if (e.key === "ArrowRight") navigateLightbox("next");
      if (e.key === "Escape") setLightboxOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, displayPhotos.length]);

  const uploadToSupabase = async (file: File): Promise<string> => {
    if (!projectId && !dealId) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.readAsDataURL(file);
      });
    }

    const fileExt = file.name.split(".").pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `projects/${projectId || dealId}/photos/${fileName}`;

    const { error } = await supabase.storage
      .from("project-assets")
      .upload(filePath, file);

    if (error) throw error;

    const {
      data: { publicUrl },
    } = supabase.storage.from("project-assets").getPublicUrl(filePath);

    return publicUrl;
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !canUpload || uploading) return;

    setUploading(true);
    try {
      const remainingSlots = maxUploads - uploadedCount;
      const filesToProcess = Array.from(files).slice(0, remainingSlots);
      const newUploadedPhotos: string[] = [];

      for (const file of filesToProcess) {
        const url = await uploadToSupabase(file);
        newUploadedPhotos.push(url);
      }

      const updatedPhotos = [
        ...photos.filter((p) => (typeof p === "string" ? p : p.url)),
        ...newUploadedPhotos,
      ];

      onPhotosChange(updatedPhotos);

      if (dealId) {
        await patchProjectKpis(dealId, { photos: updatedPhotos });
      }
    } catch (error) {
      console.error("Upload error:", error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemove = async (id: string) => {
    if (id === "main-photo-url-virtual") return;

    try {
      const photoToRemove = normalizedPhotos.find((p) => p.id === id);

      const updatedPhotos = photos.filter((p) => {
        if (typeof p === "string") {
          return p !== photoToRemove?.url;
        }
        return p.id !== id;
      });

      onPhotosChange(updatedPhotos);

      if (dealId) {
        await patchProjectKpis(dealId, { photos: updatedPhotos });
      }

      if (photoToRemove?.url?.includes("project-assets")) {
        const path = photoToRemove.url.split("project-assets/").pop();
        if (path) await supabase.storage.from("project-assets").remove([path]);
      }
    } catch (error) {
      console.error("Remove error:", error);
    }
  };

  const navigateLightbox = (direction: "prev" | "next") => {
    setSelectedIndex((prev) => {
      if (direction === "prev")
        return prev === 0 ? displayPhotos.length - 1 : prev - 1;
      return prev === displayPhotos.length - 1 ? 0 : prev + 1;
    });
  };

  const heroPhoto = displayPhotos[selectedIndex];

  return (
    <div className="space-y-4">
      <div
        className={cn(
          "relative aspect-[16/9] rounded-lg overflow-hidden bg-slate-100 border border-slate-200",
          displayPhotos.length > 0 && "cursor-pointer group",
        )}
        onClick={() => displayPhotos.length > 0 && setLightboxOpen(true)}
      >
        {displayPhotos.length > 0 ? (
          heroPhoto && (
            <>
              <img
                src={heroPhoto.url}
                alt="Property"
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
                onError={(e) => {
                  e.currentTarget.src =
                    "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNFNUU1RTUiLz48cGF0aCBkPSJNMTIwIDEwMEwxMDAgODAgODAgMTAwbDIwIDIwIDIwLTIwIDIwIDIwIDIwLTIwTDEyMCAxMDBaTTEwMCAxNDBBNDAgNDAgMCAxIDAgMTAwIDYwYTQwIDQwIDAgMCAwIDAgODBaIiBmaWxsPSIjQzVDNUM1Ii8+PC9zdmc+";
                }}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
            </>
          )
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-slate-400">
            <Image className="h-12 w-12 mb-2 opacity-20" />
            <p className="text-sm">No photos available</p>
          </div>
        )}
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 items-center">
        {canUpload && (
          <div className="flex-shrink-0">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileUpload}
              className="hidden"
              disabled={uploading}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-16 h-16 rounded-md border-2 border-dashed border-slate-300 hover:border-blue-500 hover:bg-blue-50 flex flex-col items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
              ) : (
                <>
                  <Upload className="h-4 w-4 text-slate-400" />
                  <span className="text-[10px] font-medium text-slate-500 mt-1">
                    Add
                  </span>
                </>
              )}
            </button>
          </div>
        )}

        {displayPhotos.map((photo, index) => (
          <div key={photo.id} className="relative flex-shrink-0 group">
            <button
              onClick={() => setSelectedIndex(index)}
              className={cn(
                "w-16 h-16 rounded-md overflow-hidden border-2 transition-all",
                index === selectedIndex
                  ? "border-blue-600 ring-2 ring-blue-100"
                  : "border-transparent hover:border-slate-300",
              )}
            >
              <img
                src={photo.url}
                className="w-full h-full object-cover"
                alt=""
                onError={(e) => {
                  e.currentTarget.src =
                    "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNFNUU1RTUiLz48cGF0aCBkPSJNMTIwIDEwMEwxMDAgODAgODAgMTAwbDIwIDIwIDIwLTIwIDIwIDIwIDIwLTIwTDEyMCAxMDBaTTEwMCAxNDBBNDAgNDAgMCAxIDAgMTAwIDYwYTQwIDQwIDAgMCAwIDAgODBaIiBmaWxsPSIjQzVDNUM1Ii8+PC9zdmc+";
                }}
              />
              {photo.id === "main-photo-url-virtual" && (
                <div className="absolute inset-x-0 bottom-0 bg-blue-600/90 text-[8px] text-white py-0.5 font-bold text-center">
                  URL
                </div>
              )}
            </button>

            {photo.id !== "main-photo-url-virtual" && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(photo.id);
                }}
                className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                title="Remove photo"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        ))}
      </div>

      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-5xl p-0 bg-black/95 border-none">
          <div className="relative flex items-center justify-center min-h-[50vh]">
            {heroPhoto && (
              <img
                src={heroPhoto.url}
                className="max-w-full max-h-[85vh] object-contain"
                alt=""
                onError={(e) => {
                  e.currentTarget.src =
                    "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNFNUU1RTUiLz48cGF0aCBkPSJNMTIwIDEwMEwxMDAgODAgODAgMTAwbDIwIDIwIDIwLTIwIDIwIDIwIDIwLTIwTDEyMCAxMDBaTTEwMCAxNDBBNDAgNDAgMCAxIDAgMTAwIDYwYTQwIDQwIDAgMCAwIDAgODBaIiBmaWxsPSIjQzVDNUM1Ii8+PC9zdmc+";
                }}
              />
            )}
            {displayPhotos.length > 1 && (
              <>
                <button
                  onClick={() => navigateLightbox("prev")}
                  className="absolute left-4 p-2 bg-white/10 rounded-full text-white hover:bg-white/20 transition-colors"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  onClick={() => navigateLightbox("next")}
                  className="absolute right-4 p-2 bg-white/10 rounded-full text-white hover:bg-white/20 transition-colors"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
